<template>
    <Layout :header-props="{hideHamburger: true, hideLoginButton: true}">
        <!-- Matches the LeadGen processing page exactly (per product direction): shared
             generic header (no loan amount/checkmark), spinner, single static message,
             no progress bar, no cycling messages (#5654). -->
        <div class="leadgen-processing-container mt-14 p-5">
            <div class="max-w-2xl mx-auto">
                <div class="bg-white border border-gray-200 rounded-lg shadow-lg p-8">
                    <div class="text-center">
                        <div class="spinner-container">
                            <svg class="spinner" viewBox="0 0 50 50">
                                <circle
                                    class="spinner-path"
                                    cx="25"
                                    cy="25"
                                    r="20"
                                    fill="none"
                                    stroke-width="4"
                                ></circle>
                            </svg>
                        </div>

                        <h3 class="processing-title">{{ loadingMessage }}</h3>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<script>
import Layout from '../../../signup/step/common/Layout.vue';
import { canCapturePrincipalEvidence } from './logrocketPrincipal';
import {
    LOGROCKET_SESSION_SOURCE,
    isValidLogRocketSessionUrl,
    readCachedLogRocketSessionCandidate,
    requestActiveLogRocketSessionCandidate,
} from './logrocketSessionCache';

// Which capture path outranks which. A URL read from the SDK on THIS page is the only
// one that proves the borrower was being recorded during this handoff; a cache entry or
// another tab's answer is weaker evidence and must never displace it.
const SESSION_SOURCE_RANK = {
    [LOGROCKET_SESSION_SOURCE.LIVE_PAGE]: 3,
    [LOGROCKET_SESSION_SOURCE.ACTIVE_TAB]: 2,
    [LOGROCKET_SESSION_SOURCE.RECENT_PAGE]: 1,
};
import { createMarketplaceHandoffCoordinator } from './marketplaceHandoffCoordinator';
import { HANDOFF_CAPTURE_GRACE_MS, HANDOFF_CAPTURE_STATE, submitWithCaptureGrace } from './handoffCaptureGrace';

// The capture path that produced the URL being submitted, in the shared diagnostics
// vocabulary. Log-only on the server, so it can describe what happened without ever
// being something the handoff depends on.
const CAPTURE_STATE_BY_SOURCE = {
    [LOGROCKET_SESSION_SOURCE.LIVE_PAGE]: HANDOFF_CAPTURE_STATE.LIVE,
    [LOGROCKET_SESSION_SOURCE.ACTIVE_TAB]: HANDOFF_CAPTURE_STATE.ACTIVE_TAB,
    [LOGROCKET_SESSION_SOURCE.RECENT_PAGE]: HANDOFF_CAPTURE_STATE.CACHE,
};

export default {
    components: { Layout },

    props: ['accounts', 'loan', 'handoffUrl', 'marketplaceSaleId'],

    data() {
        return {
            // Owns polling, Echo and the attempt limit; see marketplaceHandoffCoordinator.
            coordinator: null,
            // Bound listener for the browser event auth.js dispatches on MarketplaceSold.
            marketplaceSoldListener: null,
            // Set the moment the handoff form is submitted, so the DOM submit is
            // idempotent even independently of the coordinator's own latch.
            handoffSubmitted: false,
            // Session recorded for THIS handoff, captured best-effort on mount. Stays
            // null when LogRocket is absent, blocked, or still silent at redirect time.
            logRocketSessionUrl: null,
            // Where that session came from and when, in the server's vocabulary, so the
            // stored snapshot says what it actually proves.
            logRocketSessionSource: null,
            logRocketSessionCapturedAt: null,
            // Settles when the live SDK callback has answered - or immediately when
            // nothing can answer - so the submit has something bounded to wait on.
            liveCaptureSettled: null,
            // Set only when capture could not run at all, so a null session can say which
            // of its very different causes it was.
            logRocketCaptureBlocked: null,
            // Polling cadence and the 10-minute limit now live in the coordinator
            // (DEFAULT_POLL_INTERVAL_MS / DEFAULT_MAX_ATTEMPTS), so there is one place
            // that decides when waiting ends.
            loadingMessage: 'Please wait while we look at all your loan options. This may take up to a few minutes, please do not close this window or click the back button',
        }
    },

    methods: {
        canCaptureLogRocketPrincipalEvidence() {
            try {
                return canCapturePrincipalEvidence(window.marketplaceLogRocketPrincipalState);
            } catch (error) {
                return false;
            }
        },

        // Ask LogRocket for this visit's session URL as early as possible so a value is
        // already in hand when the buyer handoff fires. Deliberately fire-and-forget:
        // the SDK may be missing (disabled/blocked/local), may answer after we have
        // already left, or may throw — none of that may affect the redirect.
        //
        // Returns a promise that settles when the live callback has delivered this page's
        // own session, or at once when no callback can ever come. It is only a signal to
        // stop waiting: the ranking below still decides what is actually submitted.
        captureLogRocketSession() {
            let settleLiveCapture = () => {};
            const liveCaptureSettled = new Promise(resolve => {
                settleLiveCapture = resolve;
            });

            // An unsafe/unknown principal boundary submits honest-null evidence. Do not
            // read the live SDK, cache, or active tabs under a principal we cannot prove.
            if (!this.canCaptureLogRocketPrincipalEvidence()) {
                this.logRocketCaptureBlocked = HANDOFF_CAPTURE_STATE.PRINCIPAL_UNSAFE;
                settleLiveCapture();

                return liveCaptureSettled;
            }

            let logRocket = null;

            try {
                logRocket = window.LogRocket;
            } catch (error) {
                // A blocked SDK means there is no current recording to associate.
            }

            if (!logRocket || typeof logRocket.getSessionURL !== 'function') {
                this.logRocketCaptureBlocked = HANDOFF_CAPTURE_STATE.NO_SDK;
                settleLiveCapture();

                return liveCaptureSettled;
            }

            const cacheContext = {
                userId: this.loan?.user_id,
                loanId: this.loan?.id,
                project: window.marketplaceLogRocketProject || null,
            };

            // Every source produces the same {url, source, capturedAt} shape, and a
            // weaker one can never displace a stronger one — so what the server stores is
            // the capture path that actually happened, whatever order the answers arrive
            // in (the live SDK callback can resolve after a cached value was accepted).
            const offerCandidate = candidate => {
                if (!candidate?.url || !isValidLogRocketSessionUrl(candidate.url, cacheContext.project)) {
                    return;
                }

                const rank = SESSION_SOURCE_RANK[candidate.source] || 0;
                const currentRank = SESSION_SOURCE_RANK[this.logRocketSessionSource] || 0;

                if (this.logRocketSessionUrl && rank < currentRank) {
                    return;
                }

                this.logRocketSessionUrl = candidate.url;
                this.logRocketSessionSource = candidate.source;
                this.logRocketSessionCapturedAt = Number.isFinite(candidate.capturedAt)
                    ? new Date(candidate.capturedAt).toISOString()
                    : new Date().toISOString();
            };

            const useCurrentPageSession = sessionUrl => {
                offerCandidate({
                    // Keep the SDK URL exactly as given, including its current ?t= offset.
                    url: sessionUrl,
                    source: LOGROCKET_SESSION_SOURCE.LIVE_PAGE,
                    capturedAt: Date.now(),
                });

                // Only an accepted live_page URL ends the wait. A callback that hands back
                // something unusable leaves the grace to expire on its own, exactly as if
                // it had never answered.
                if (this.logRocketSessionSource === LOGROCKET_SESSION_SOURCE.LIVE_PAGE) {
                    settleLiveCapture();
                }
            };

            try {
                // LogRocket exposes this synchronous integration value once booted. It
                // is stronger evidence than any cache and often avoids waiting at all.
                useCurrentPageSession(logRocket.sessionURL);
            } catch (error) {
                // Continue to the isolated cache/async fallbacks below.
            }

            // A cache entry is accepted synchronously only inside the away window since
            // this browser last had the page visible, and only for this user + loan.
            offerCandidate(readCachedLogRocketSessionCandidate(cacheContext));

            if (!this.logRocketSessionUrl) {
                // For older recovery-SMS returns, ask every still-open account tab and
                // take the freshest answer. This never delays the handoff.
                requestActiveLogRocketSessionCandidate(cacheContext)
                    .then(offerCandidate)
                    .catch(() => {});
            }

            try {
                logRocket.getSessionURL(useCurrentPageSession);
            } catch (error) {
                // Observability is not allowed to break the handoff, and nothing will
                // call back now, so nothing may be waited for either.
                this.logRocketCaptureBlocked = HANDOFF_CAPTURE_STATE.NO_SDK;
                settleLiveCapture();
            }

            return liveCaptureSettled;
        },

        // What the capture had managed by the time this handoff was posted. A null session
        // on its own says only that something went wrong; this says which of several very
        // different things it was, and the server does nothing with it but log it.
        logRocketCaptureState() {
            if (this.logRocketSessionUrl) {
                return CAPTURE_STATE_BY_SOURCE[this.logRocketSessionSource]
                    || HANDOFF_CAPTURE_STATE.UNKNOWN_SOURCE;
            }

            // Nothing captured and nothing blocking it: the callback outlived its grace.
            return this.logRocketCaptureBlocked || HANDOFF_CAPTURE_STATE.GRACE_TIMEOUT;
        },

        // Leave a marker in the replay at the moment we hand the applicant over. This
        // says the app started the handoff — it is NOT evidence the buyer's page loaded
        // or that they landed anywhere; there is no callback from the buyer.
        trackRedirectInitiated(marketplaceSaleId) {
            if (!this.canCaptureLogRocketPrincipalEvidence()) {
                return;
            }

            try {
                window.LogRocket?.track?.('marketplace_redirect_initiated', {
                    marketplace_sale_id: marketplaceSaleId ?? null,
                    loan_id: this.loan?.id ?? null,
                });
            } catch (error) {
                // Best-effort only: never delay or gate the mark request or navigation.
            }
        },

        // Perform the handoff: one real browser POST to the signed action, whose response
        // IS the navigation to the buyer (the server answers 303).
        //
        // This replaces a fetch() followed by an unrelated window.location assignment.
        // That pair could navigate even when the claim failed, could navigate twice, and
        // left a window where the sale was consumed but nobody was sent anywhere. Here
        // the claim and the navigation are the same request/response, so they cannot come
        // apart — and the destination is chosen by the server, not by this page.
        submitHandoff({ handoffUrl, marketplaceSaleId }) {
            // The coordinator has already latched; this second guard only makes the DOM
            // side idempotent in its own right.
            if (this.handoffSubmitted || !handoffUrl) {
                return;
            }

            this.handoffSubmitted = true;
            this.trackRedirectInitiated(marketplaceSaleId ?? this.marketplaceSaleId);

            // The pre-resolved path leaves in about a second, while the SDK issues this
            // page's session URL 1-5s after init - which is why 68% of production claims
            // carried no session, and why a callback landing after the form was submitted
            // could never be attached to anything. Give it a bounded 2s, then submit with
            // whatever is held: a missing recording still never costs the handoff.
            return submitWithCaptureGrace(
                this.liveCaptureSettled,
                () => this.postHandoffForm(handoffUrl),
                {
                    maxWaitMs: HANDOFF_CAPTURE_GRACE_MS,
                    hasUrl: () => !!this.logRocketSessionUrl,
                },
            );
        },

        // The handoff POST itself: one real browser submit whose response is the
        // navigation. Split out so the capture grace above has something to hand in.
        postHandoffForm(handoffUrl) {
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = handoffUrl;
            form.style.display = 'none';

            // Optional telemetry is omitted rather than sent empty: a missing recording
            // must never be a reason the form fails to submit.
            const addField = (name, value) => {
                if (value === null || value === undefined || value === '') {
                    return;
                }

                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                input.value = value;
                form.appendChild(input);
            };

            addField('_token', document.head.querySelector('meta[name="csrf-token"]')?.content || '');
            addField('logrocket_session_url', this.logRocketSessionUrl);
            addField('logrocket_session_source', this.logRocketSessionSource);
            addField('logrocket_session_captured_at', this.logRocketSessionCapturedAt);
            addField('logrocket_capture_state', this.logRocketCaptureState());

            document.body.appendChild(form);
            form.submit();
        },

        // auth.js offers every MarketplaceSold event to a mounted owner before falling
        // back to its global reload. The coordinator decides whether this page owns it;
        // claiming the browser event is what suppresses that reload.
        handleMarketplaceSold(event) {
            const detail = event.detail || {};

            if (this.coordinator && this.coordinator.handleSoldEvent(detail)) {
                event.preventDefault();
                this.coordinator.noteStaleSale(detail.marketplaceSaleId, this.marketplaceSaleId);
            }
        },
    },

    mounted() {
        // One owner for polling, Echo and the attempt limit. Every effect it can have on
        // the page is handed in here, which is what makes its races testable in isolation
        // (marketplaceHandoffCoordinator.test.js).
        this.coordinator = createMarketplaceHandoffCoordinator({
            loanId: this.loan?.id,
            checkStatus: () => this.$http
                .get(`/signup/check-marketplace-status/${this.loan.id}`)
                .then(response => response.data),
            submitHandoff: payload => this.submitHandoff(payload),
            reload: () => window.location.reload(),
            onNotice: notice => console.warn('[marketplace handoff]', notice.reason, notice),
        });

        // Registered before either branch so an Echo event that arrives during the
        // pre-resolved path's short delay is still claimed rather than reloading.
        this.marketplaceSoldListener = event => this.handleMarketplaceSold(event);
        window.addEventListener('marketplace:sold', this.marketplaceSoldListener);

        // Start capturing before either branch: the pre-resolved path leaves in ~1s, and
        // its submit waits on the promise this returns.
        this.liveCaptureSettled = this.captureLogRocketSession();

        // Sale already resolved server-side (the page was rendered with a signed action):
        // submit it. Don't start polling — it would race the handoff.
        if (this.handoffUrl) {
            this.loadingMessage = 'Congratulations! Redirecting you to your offer...';
            this.coordinator.startWithResolvedHandoff({
                handoff_url: this.handoffUrl,
                marketplace_sale_id: this.marketplaceSaleId,
            });

            return;
        }

        this.coordinator.startPolling();
    },

    beforeDestroy() {
        if (this.coordinator) {
            this.coordinator.stop();
        }

        if (this.marketplaceSoldListener) {
            window.removeEventListener('marketplace:sold', this.marketplaceSoldListener);
            this.marketplaceSoldListener = null;
        }
    },
}
</script>

<style scoped>
/* Styles matching the LeadGen processing page (resources/js/signup/leadgen/LeadgenProcessing.vue) */
.leadgen-processing-container {
    min-height: calc(100vh - 200px);
}

.spinner-container {
    display: flex;
    justify-content: center;
    margin-bottom: 1.75rem;
}

.spinner {
    width: 60px;
    height: 60px;
    animation: rotate 2s linear infinite;
}

.spinner-path {
    stroke: #f97316;
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
}

.processing-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.75rem 0;
    line-height: 1.5;
    max-width: 100%;
    padding: 0 0.5rem;
}

@keyframes rotate {
    100% {
        transform: rotate(360deg);
    }
}

@keyframes dash {
    0% {
        stroke-dasharray: 1, 150;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -35;
    }
    100% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -124;
    }
}
</style>
